import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties, FC } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Badge, Popover } from 'antd'
import { MessageOutlined, MailOutlined } from '@ant-design/icons'

import { rqKeys } from '@/react-query-keys'
import { formatTs } from '@/helpers'
import {
  findVisitorMessageById,
  markVisitorMessagesAsRead,
  useMessagesStore,
} from '../index'

export const MessagesNotifier: FC = () => {
  const queryClient = useQueryClient()
  const [ isPopoverOpen, setIsPopoverOpen ] = useState(false)
  const [ isCompactViewport, setIsCompactViewport ] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(max-width: 480px)').matches
  })

  const unreadMessages = useMessagesStore((s) => s.unreadMessages)
  const isUnreadInitialized = useMessagesStore((s) => s.isUnreadInitialized)
  const markMessagesAsRead = useMessagesStore((s) => s.markMessagesAsRead)
  const openMessenger = useMessagesStore((s) => s.openMessenger)

  const unreadCount = unreadMessages.length

  useEffect(() => {
    if (typeof window === 'undefined') return

    const media = window.matchMedia('(max-width: 480px)')
    const onChange = (event: MediaQueryListEvent) => setIsCompactViewport(event.matches)

    setIsCompactViewport(media.matches)
    media.addEventListener('change', onChange)

    return () => media.removeEventListener('change', onChange)
  }, [])

  const previewItems = useMemo(() => {
    return [ ...unreadMessages ]
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 8)
  }, [ unreadMessages ])

  const openTopic = (topicId?: number | null, messageId?: number | null) => {
    setIsPopoverOpen(false)
    openMessenger({
      topicId: topicId ?? null,
      messageId: messageId ?? null,
    })
  }

  const markSingleMessageAsRead = (messageId: number) => {
    if (!messageId) return

    markMessagesAsRead([ messageId ])
    void markVisitorMessagesAsRead([ messageId ]).catch((error) => {
      console.warn('[messages] failed to mark message as read from notifier', error)
      void queryClient.invalidateQueries({ queryKey: [ rqKeys.visitorMessageUnread ], exact: false })
    })
  }

  const openAndMarkMessage = async (messageId: number, topicId: number) => {
    if (!messageId) return
    markSingleMessageAsRead(messageId)

    const normalizedTopicId = Number(topicId)
    if (Number.isFinite(normalizedTopicId) && normalizedTopicId > 0) {
      openTopic(normalizedTopicId, messageId)
      return
    }

    try {
      const found = await findVisitorMessageById(messageId)
      const resolvedTopicId = Number(found?.topic?.id)
      if (Number.isFinite(resolvedTopicId) && resolvedTopicId > 0) {
        openTopic(resolvedTopicId, messageId)
        return
      }
    } catch (error) {
      console.warn('[messages] failed to resolve topic for unread message', error)
    }

    openTopic(null, messageId)
  }

  const sectionTitleStyle: CSSProperties = {
    color: '#2E2F33',
    fontSize: 18,
    fontWeight: 600,
    lineHeight: '22px',
    fontFamily: 'inherit',
  }
  const allMessagesStyle: CSSProperties = {
    ...sectionTitleStyle,
    fontWeight: 400,
  }

  const content = (
    <div style={{ width: isCompactViewport ? '100%' : 320, maxWidth: '100%' }}>
      <div
        style={{
          ...sectionTitleStyle,
          marginBottom: 8,
        }}
      >
        Сообщения
      </div>

      {!isUnreadInitialized ? (
        <div style={{ color: '#9C9CA1', fontSize: 13 }}>Загрузка...</div>
      ) : (
        <>
          {previewItems.length ? (
            <div style={{ maxHeight: 350, overflow: 'auto' }}>
              {previewItems.map((item) => (
                <button
                  type='button'
                  key={item.id}
                  onClick={() => {
                    void openAndMarkMessage(item.id, item.topic.id)
                  }}
                  style={{
                    width: '100%',
                    border: 0,
                    textAlign: 'left',
                    borderRadius: 10,
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: '8px 6px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}
                >
                  <MailOutlined style={{ marginTop: 2, color: '#666' }} />

                  <div style={{ minWidth: 0, width: '100%' }}>
                    <div
                      style={{
                        fontSize: 14,
                        lineHeight: '18px',
                        color: '#2E2F33',
                        marginBottom: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.content || item.topic.subject || 'Сообщение'}
                    </div>
                    <div style={{ fontSize: 12, color: '#9C9CA1' }}>
                      {formatTs(item.timestamp.getTime())}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ color: '#9C9CA1', fontSize: 13, marginBottom: 8 }}>
              Новых сообщений нет
            </div>
          )}

          <button
            type='button'
            onClick={() => openTopic(null, null)}
            style={{
              width: '100%',
              border: 0,
              borderTop: '1px solid #F0F0F0',
              marginTop: 8,
              background: 'transparent',
              cursor: 'pointer',
              padding: '10px 0 4px',
              font: 'inherit',
              WebkitAppearance: 'none',
              appearance: 'none',
              textAlign: 'left',
            }}
          >
            <span style={allMessagesStyle}>Все сообщения</span>
          </button>
        </>
      )}
    </div>
  )

  return (
    <Popover
      trigger='click'
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      content={content}
      placement={isCompactViewport ? 'bottom' : 'bottomRight'}
      overlayStyle={isCompactViewport
        ? {
          width: '80vw',
          maxWidth: '80vw',
          left: '50%',
          transform: 'translateX(-50%)',
        }
        : undefined}
    >
      <button
        type='button'
        title='Сообщения'
        style={{
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          color: '#191919',
        }}
      >
        <Badge count={unreadCount} overflowCount={99} size='small'>
          <MessageOutlined style={{ fontSize: 20 }} />
        </Badge>
      </button>
    </Popover>
  )
}
